import React, { useState } from "react";
import axios from "axios";

function HeaderVideoUploader() {
  const [formData, setFormData] = useState({
    id: "",
    category: "",
    genre: "",
    type: "",
  });

  const [videoFile, setVideoFile] = useState(null);
  const [coverImage, setCoverImage] = useState(null);
  const [videoUploadStatus, setVideoUploadStatus] = useState("");
  const [coverUploadStatus, setCoverUploadStatus] = useState("");
  const [videoProgress, setVideoProgress] = useState(0);
  const [coverProgress, setCoverProgress] = useState(0);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [isUploadingCover, setIsUploadingCover] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleVideoChange = (e) => setVideoFile(e.target.files[0]);
  const handleCoverChange = (e) => setCoverImage(e.target.files[0]);

  // 🎥 Upload video only
  const handleVideoUpload = async () => {
    if (!videoFile) {
      setVideoUploadStatus("❌ Please select a video file first.");
      return;
    }

    setVideoUploadStatus("🎥 Uploading video...");
    setVideoProgress(0);
    setIsUploadingVideo(true);

    const videoData = new FormData();
    Object.entries(formData).forEach(([key, value]) => videoData.append(key, value));
    videoData.append("video", videoFile); // ✅ match backend

    try {
      const response = await axios.post(
        "https://api.malidag.com/upload-header-video",
        videoData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          maxBodyLength: Infinity,
          onUploadProgress: (e) =>
            setVideoProgress(Math.round((e.loaded * 100) / e.total)),
        }
      );

      setVideoUploadStatus("✅ Video uploaded successfully!");
      console.log("🎥 Video response:", response.data);
    } catch (error) {
      console.error("❌ Error uploading video:", error);
      setVideoUploadStatus("❌ Upload failed. Check console for details.");
    } finally {
      setIsUploadingVideo(false);
    }
  };

  // 🖼️ Upload cover only
  const handleCoverUpload = async () => {
    if (!coverImage) {
      setCoverUploadStatus("❌ Please select a cover image first.");
      return;
    }

    setCoverUploadStatus("🖼️ Uploading cover image...");
    setCoverProgress(0);
    setIsUploadingCover(true);

    const coverData = new FormData();
    coverData.append("id", formData.id);
    coverData.append("category", formData.category);
    coverData.append("genre", formData.genre);
    coverData.append("type", formData.type);
    coverData.append("image", coverImage); // ✅ match backend

    try {
      const response = await axios.post(
        "https://api.malidag.com/upload-cover-image",
        coverData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) =>
            setCoverProgress(Math.round((e.loaded * 100) / e.total)),
        }
      );

      setCoverUploadStatus("✅ Cover image uploaded successfully!");
      console.log("🖼️ Cover response:", response.data);
    } catch (error) {
      console.error("❌ Error uploading cover image:", error);
      setCoverUploadStatus("❌ Upload failed. Check console for details.");
    } finally {
      setIsUploadingCover(false);
    }
  };

  return (
    <div style={{ maxWidth: "500px", margin: "auto", padding: "20px" }}>
      <h1>Upload Header Video & Cover</h1>

      {/* Text inputs */}
      {["id", "category", "genre", "type"].map((field) => (
        <div key={field} style={{ marginBottom: "10px" }}>
          <label style={{ textTransform: "capitalize" }}>{field}:</label>
          <input
            type="text"
            name={field}
            value={formData[field]}
            onChange={handleChange}
            required
            style={{ width: "100%", padding: "8px", marginTop: "5px" }}
            disabled={isUploadingVideo || isUploadingCover}
          />
        </div>
      ))}

      {/* 🎥 Video uploader */}
      <div style={{ marginBottom: "20px" }}>
        <label>Upload Video:</label>
        <input
          type="file"
          accept="video/mp4,video/webm,video/quicktime"
          onChange={handleVideoChange}
          style={{ width: "100%", padding: "8px", marginTop: "5px" }}
          disabled={isUploadingVideo}
        />
        <button
          onClick={handleVideoUpload}
          disabled={isUploadingVideo}
          style={{
            marginTop: "10px",
            padding: "10px 20px",
            cursor: isUploadingVideo ? "not-allowed" : "pointer",
            backgroundColor: isUploadingVideo ? "#6c757d" : "#007bff",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
          }}
        >
          {isUploadingVideo ? "Uploading..." : "Upload Video"}
        </button>

        {isUploadingVideo && (
          <div
            style={{
              marginTop: "10px",
              height: "8px",
              width: "100%",
              background: "#eee",
              borderRadius: "4px",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${videoProgress}%`,
                background: "#007bff",
                borderRadius: "4px",
                transition: "width 0.3s ease",
              }}
            />
          </div>
        )}
        {videoUploadStatus && (
          <p style={{ marginTop: "8px", fontWeight: "bold" }}>
            {videoUploadStatus}
          </p>
        )}
      </div>

      {/* 🖼️ Cover uploader */}
      <div>
        <label>Upload Cover Image:</label>
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={handleCoverChange}
          style={{ width: "100%", padding: "8px", marginTop: "5px" }}
          disabled={isUploadingCover}
        />
        <button
          onClick={handleCoverUpload}
          disabled={isUploadingCover}
          style={{
            marginTop: "10px",
            padding: "10px 20px",
            cursor: isUploadingCover ? "not-allowed" : "pointer",
            backgroundColor: isUploadingCover ? "#6c757d" : "#28a745",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
          }}
        >
          {isUploadingCover ? "Uploading..." : "Upload Cover"}
        </button>

        {isUploadingCover && (
          <div
            style={{
              marginTop: "10px",
              height: "8px",
              width: "100%",
              background: "#eee",
              borderRadius: "4px",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${coverProgress}%`,
                background: "#28a745",
                borderRadius: "4px",
                transition: "width 0.3s ease",
              }}
            />
          </div>
        )}
        {coverUploadStatus && (
          <p style={{ marginTop: "8px", fontWeight: "bold" }}>
            {coverUploadStatus}
          </p>
        )}
      </div>
    </div>
  );
}

export default HeaderVideoUploader;
